import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { PageEmpty } from '../../performance-operations/styles';
import { TEAM_VIEW_DISABLED_KEY } from '../../../services/performanceFeedbackAnalyticsService';
import { formatDateTime } from '../../../utils/Utils';
import { RECORD_TYPES, fetchRecords } from '../config';

// Analiz §16 E13 alt alan — yetkili kapsamda kayıt listesi: tarih, konu, paylaşım düzeyi, çalışan, gönderen ve takip (talep
// durumu / talep üzerine). YALNIZ ÜST VERİ: metin, anonim gönderici, talep mesajı ve 1:1 hiçbir zaman listede değildir; özel
// takdir listede hiç görünmez. Kişi kümesiyle sınırlı (İK kiracı; yönetici ekibi — mevcut "ekibin geri bildirimlerini görür"
// ayarı kapalıysa liste kapalıdır). Sunucu tarafı sayfalama; tek kaydırma (yalnız tablo satırları kayar).
const Detail = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Key = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Note = styled.p`
  margin: 16px 0 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

export default function RecordsTab({ params, recordType, onRecordType, height, isHr }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [query, setQuery] = useState({ pageNumber: 1, pageSize: 20 });
  const [state, setState] = useState({ loading: true, error: false, disabled: false, rows: [], total: 0 });
  const [attempt, setAttempt] = useState(0);
  const [detail, setDetail] = useState(null);
  const key = JSON.stringify(params);

  useEffect(() => { setQuery((q) => ({ ...q, pageNumber: 1 })); }, [key, recordType]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetchRecords(params, recordType, query.pageNumber, query.pageSize, TEAM_VIEW_DISABLED_KEY).then((r) => {
      if (!alive) return;
      if (r.ok) setState({ loading: false, error: false, disabled: false, rows: r.data?.data || [], total: r.data?.totalRowCount || 0 });
      else setState({ loading: false, error: !r.teamViewDisabled, disabled: !!r.teamViewDisabled, rows: [], total: 0 });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, recordType, query, attempt]);

  const rows = useMemo(() => state.rows.map((r) => ({
    ...r,
    dateText: formatDateTime(r.date, lang),
    topicText: r.topic ? t(`performance.feedbackAnalytics.topic.${r.topic}`, { defaultValue: r.topic }) : '-',
    shareText: t(`performance.feedbackAnalytics.share.${r.shareLevel}`),
    senderText: r.senderName || (r.shareLevel === 'anonymous' ? t('performance.feedbackAnalytics.share.anonymous') : '-'),
    followText: r.recordType === 'request'
      ? t(`performance.feedbackAnalytics.requestStatus.${r.status}`, { defaultValue: r.status || '-' })
      : (r.viaRequest ? t('performance.feedbackAnalytics.viaRequest') : '-'),
  })), [state.rows, t, lang]);

  const actions = useMemo(() => [
    { type: 'details', label: t('common.details'), onClick: (row) => setDetail(row) },
  ], [t]);

  const columns = useMemo(() => [
    { name: 'dateText', label: t('performance.feedbackAnalytics.col.date'), options: { filter: false, sort: false } },
    { name: 'topicText', label: t('performance.feedbackAnalytics.col.topic'), options: { filter: false, sort: false } },
    { name: 'shareText', label: t('performance.feedbackAnalytics.col.share'), options: { filter: false, sort: false } },
    { name: 'receiverName', label: recordType === 'request' ? t('performance.feedbackAnalytics.col.requester') : t('performance.feedbackAnalytics.col.receiver'), options: { filter: false, sort: false } },
    { name: 'receiverDepartment', label: t('performance.feedbackAnalytics.col.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'senderText', label: recordType === 'request' ? t('performance.feedbackAnalytics.col.target') : t('performance.feedbackAnalytics.col.sender'), options: { filter: false, sort: false } },
    { name: 'followText', label: t('performance.feedbackAnalytics.col.followUp'), options: { filter: false, sort: false } },
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => <OptionsMenu actions={actions} row={rows[tableMeta.rowIndex]} />,
      },
    },
  ], [t, recordType, actions, rows]);

  const onTableChanged = useCallback((action, tableState) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') {
      setQuery({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || 20 });
    }
  }, []);

  const typeSwitch = (
    <>
      {RECORD_TYPES.map((rt) => (
        <Button key={rt} variant={recordType === rt ? 'primary' : 'ghost'} onClick={() => onRecordType(rt)} data-record-type={rt}>
          {t(`performance.feedbackAnalytics.recordType.${rt}`)}
        </Button>
      ))}
    </>
  );

  if (state.disabled) {
    return <PageEmpty data-state="team-view-disabled">{t('performance.feedbackAnalytics.teamViewDisabled')}</PageEmpty>;
  }
  if (state.error) {
    return (
      <PageEmpty data-state="error">
        <div>{t('performance.operations.panel.error')}</div>
        <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry">{t('performance.operations.panel.retry')}</Button>
      </PageEmpty>
    );
  }

  return (
    <>
      <DataTable
        title={t('performance.feedbackAnalytics.tab.records')}
        columns={columns}
        data={rows}
        totalRowCount={state.total}
        pageSize={query.pageSize}
        pageIndex={query.pageNumber - 1}
        onTableChanged={onTableChanged}
        fetching={state.loading}
        disableFilters
        toolbarActions={typeSwitch}
        tableBodyMaxHeight={height ? `${Math.max(200, height - 150)}px` : undefined}
      />
      <GenericFormDrawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={t('performance.feedbackAnalytics.detailTitle')}
        schema={[]}
        submitLabel={t('performance.feedbackAnalytics.close')}
        onSave={async () => true}
      >
        {detail ? (
          <>
            <Detail data-detail={detail.id}>
              <Key>{t('performance.feedbackAnalytics.col.date')}</Key><div>{detail.dateText}</div>
              <Key>{t('performance.feedbackAnalytics.col.type')}</Key><div>{t(`performance.feedbackAnalytics.recordType.${detail.recordType}`)}</div>
              <Key>{t('performance.feedbackAnalytics.col.topic')}</Key><div>{detail.topicText}</div>
              <Key>{t('performance.feedbackAnalytics.col.share')}</Key><div>{detail.shareText}</div>
              <Key>{detail.recordType === 'request' ? t('performance.feedbackAnalytics.col.requester') : t('performance.feedbackAnalytics.col.receiver')}</Key><div>{detail.receiverName || '-'}</div>
              <Key>{t('performance.feedbackAnalytics.col.department')}</Key><div>{detail.receiverDepartment || '-'}</div>
              <Key>{detail.recordType === 'request' ? t('performance.feedbackAnalytics.col.target') : t('performance.feedbackAnalytics.col.sender')}</Key><div>{detail.senderText}</div>
              <Key>{t('performance.feedbackAnalytics.col.followUp')}</Key><div>{detail.followText}</div>
            </Detail>
            <Note>{t('performance.feedbackAnalytics.detailNote')}</Note>
            {isHr && detail.recordType === 'feedback' ? (
              <Button variant="outline" onClick={() => history.push('/feedback/all')} data-action="open-feedback">
                {t('performance.feedbackAnalytics.openFeedbackPage')}
              </Button>
            ) : null}
          </>
        ) : null}
      </GenericFormDrawer>
    </>
  );
}
